/**
 * LandingPage.jsx
 * ──────────────
 * Public landing page. No login required.
 * Modern redesign: hero (preserved) + organic-shape decision path +
 * staggered blob features + striped disclaimer + footer.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import QuestionnaireBanner from "../../components/QuestionnaireBanner";

/* ─── Shared design tokens (in case parent doesn't provide) ───
   Colors:
     --green-700  #3d6213    --green-600 #4B7518   --green-500 #5d8f22
     --green-400  #7da72f    --green-300 #9BC071
     --lime-400   #D4D980    --lime-300  #E8EDB4
     --brown-700  #5C4B00    --brown-500 #775F46
     --cream      #F6F9F2
*/

// ── Global styles ────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      :root {
        --green-900: #2e4d0e; --green-800: #3a5c12; --green-700: #3d6213;
        --green-600: #4B7518; --green-500: #5d8f22; --green-400: #7da72f;
        --green-300: #9BC071; --lime-500: #c4ca60; --lime-400: #D4D980;
        --lime-300:  #E8EDB4; --cream: #F6F9F2;   --cream-2: #edf5e1;
        --brown-900: #3a2800; --brown-700: #5C4B00; --brown-500: #775F46;
      }
      .bx-root, .bx-root * { box-sizing: border-box; }
      .bx-root {
        font-family: 'Nunito', system-ui, -apple-system, sans-serif;
        background: var(--cream);
        color: var(--brown-500);
        -webkit-font-smoothing: antialiased;
      }
      .bx-root a { color: inherit; text-decoration: none; }

      /* Nav */
      .bx-nav {
        background: #fff; border-bottom: 2px solid var(--lime-400);
        position: sticky; top: 0; z-index: 100;
        transition: box-shadow .35s, background .35s, backdrop-filter .35s;
      }
      .bx-nav.scrolled {
        background: rgba(255,255,255,.96); backdrop-filter: blur(12px);
        box-shadow: 0 4px 24px rgba(75,117,24,.13);
      }
      .bx-nav-inner {
        max-width: 1200px; margin: 0 auto; padding: 0 28px;
        height: 80px; display: flex; align-items: center; justify-content: space-between;
      }
      .bx-brand { display: flex; align-items: center; gap: 12px; }
      .bx-brand-mark { width: 46px; height: 46px; display: grid; place-items: center; }
      .bx-brand-text {
        font-weight: 900; font-size: 26px; color: var(--green-600);
        letter-spacing: .02em; line-height: 1;
      }
      .bx-brand-text small {
        display: block; font-size: 8.5px; letter-spacing: .42em;
        color: var(--brown-500); font-weight: 700; margin-top: 2px;
      }
      .nav-pill {
        position: relative; font-size: 16px; padding: 8px 16px; border-radius: 8px;
        font-weight: 500; color: var(--brown-500);
        transition: color .22s, background .22s, box-shadow .22s;
        overflow: hidden; display: inline-flex; align-items: center; gap: 6px;
      }
      .nav-pill::after {
        content: ''; position: absolute; bottom: 4px; left: 50%;
        transform: translateX(-50%); width: 0; height: 2px;
        background: var(--green-600); border-radius: 99px;
        transition: width .28s cubic-bezier(.4,0,.2,1);
      }
      .nav-pill:hover { color: var(--green-600); background: rgba(75,117,24,.08); }
      .nav-pill:hover::after { width: 55%; }
      .nav-pill.active {
        background: linear-gradient(135deg, var(--lime-400), #cdd46e);
        color: #4a5500; font-weight: 700;
        box-shadow: 0 2px 10px rgba(212,217,128,.5);
      }
      .nav-pill.active::after { display: none; }

      /* Buttons */
      .btn-primary {
        display: inline-flex; align-items: center; gap: 10px;
        background: linear-gradient(110deg, var(--lime-400) 0%, #edf07e 35%, var(--lime-400) 55%, var(--lime-500) 100%);
        background-size: 250% auto;
        color: #D4D980; border: none; border-radius: 12px;
        padding: 14px 28px; font-size: 14px; font-weight: 700; cursor: pointer;
        font-family: inherit; box-shadow: 0 4px 20px rgba(212,217,128,.55);
        transition: background-position .55s ease, box-shadow .25s, transform .2s;
        letter-spacing: .015em;
      }
      .btn-primary:hover {
        background-position: right center;
        box-shadow: 0 10px 34px rgba(212,217,128,.75);
        transform: translateY(-3px) scale(1.02);
      }
      .btn-primary:active { transform: translateY(0) scale(.98); }
      .btn-primary svg.search { transition: transform .3s; }
      .btn-primary:hover svg.search { transform: scale(1.2) rotate(-12deg); }

      .btn-ghost {
        display: inline-flex; align-items: center; gap: 10px;
        background: rgba(255,255,255,.10); color: #fff;
        border: 1.5px solid rgba(212,217,128,.55); border-radius: 12px;
        padding: 13px 28px; font-size: 14px; font-weight: 500; cursor: pointer;
        font-family: inherit; backdrop-filter: blur(6px);
        transition: background .25s, border-color .25s, box-shadow .25s, transform .2s;
        letter-spacing: .015em;
      }
      .btn-ghost:hover {
        background: rgba(255,255,255,.18); border-color: rgba(212,217,128,.92);
        box-shadow: 0 8px 26px rgba(0,0,0,.22); transform: translateY(-3px);
      }

      /* Reusable animations */
      @keyframes lp-drift {
        0%,100% { transform: translate(0,0) scale(1); }
        33%      { transform: translate(14px,-20px) scale(1.05); }
        66%      { transform: translate(-10px,12px) scale(.96); }
      }
      @keyframes lp-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      @keyframes lp-floatY {
        0%,100% { transform: translateY(0) rotate(0); }
        50%      { transform: translateY(-14px) rotate(2deg); }
      }
      @keyframes lp-pulse {
        0%   { box-shadow: 0 0 0 0 rgba(93,143,34,.6); }
        100% { box-shadow: 0 0 0 14px rgba(93,143,34,0); }
      }

      /* Reveal on scroll (safe-by-default: visible unless js-anim) */
      .reveal { transition: opacity .8s cubic-bezier(.2,.7,.2,1), transform .8s cubic-bezier(.2,.7,.2,1); }
      .js-anim .reveal:not(.in) { opacity: 0; transform: translateY(24px); }
      .reveal.in { opacity: 1; transform: translateY(0); }

      /* Hero */
      .lp-hero {
        background: linear-gradient(135deg, var(--green-700) 0%, var(--green-600) 55%, var(--green-500) 100%);
        position: relative; overflow: hidden;
      }
      .lp-hero-deco { position: absolute; inset: 0; pointer-events: none; }
      .lp-hero-deco .rings-tr { position: absolute; top: -20px; right: -40px; opacity: .14; }
      .lp-hero-deco .rings-bl { position: absolute; bottom: 40px; left: -60px; opacity: .08; }
      .lp-hero-deco .orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(60px); }
      .lp-hero-grid {
        max-width: 1200px; margin: 0 auto; padding: 64px 28px 0;
        display: grid; grid-template-columns: 1.2fr 1fr; gap: 36px;
        align-items: center; position: relative; z-index: 2;
      }
      .lp-hero h1 {
        font-size: clamp(34px, 4.4vw, 52px); font-weight: 900; color: #fff;
        margin: 0 0 18px; line-height: 1.08; letter-spacing: -.02em;
      }
      .lp-hero h1 .accent {
        color: var(--lime-400); font-style: italic; font-weight: 700;
        display: inline-block; position: relative;
      }
      .lp-hero h1 .accent::after {
        content: ''; position: absolute; left: 0; right: 0; bottom: 4px; height: 7px;
        background: rgba(212,217,128,.22); border-radius: 99px; z-index: -1;
      }
      .lp-hero .lead {
        font-size: 16px; color: rgba(255,255,255,.86);
        line-height: 1.75; margin: 0 0 30px; max-width: 540px;
      }
      .lp-hero-ctas { display: flex; gap: 14px; flex-wrap: wrap; padding-bottom: 56px; }
      .species { font-style: italic; color: var(--lime-400); font-weight: 600; }
      .species-d { font-style: italic; color: var(--green-600); font-weight: 600; }

      .lp-mark { display: grid; place-items: center; position: relative; }
      .lp-mark .orbit {
        position: absolute; border: 1.5px dashed rgba(212,217,128,.35);
        border-radius: 50%; animation: lp-spin 60s linear infinite;
      }
      .lp-mark .orbit.r1 { width: 360px; height: 360px; }
      .lp-mark .orbit.r2 { width: 440px; height: 440px; animation-duration: 90s; animation-direction: reverse; opacity: .6; }
      /* New inner orbit — remove .r0 element in JSX to revert */
      .lp-mark .orbit.r0 { width: 308px; height: 308px; animation-duration: 22s; border-color: rgba(212,217,128,.22); }
      .lp-mark .orbit .seed { position: absolute; width: 14px; height: 14px; border-radius: 50%; }
      .lp-mark .orbit.r1 .seed {
        top: -7px; left: 50%; transform: translateX(-50%);
        background: var(--lime-400); box-shadow: 0 0 16px var(--lime-400);
      }
      .lp-mark .orbit.r2 .seed {
        bottom: -7px; left: 35%;
        background: var(--green-300); box-shadow: 0 0 16px var(--green-300);
      }
      .lp-mark .orbit.r0 .seed {
        top: 18%; right: -5px;
        width: 10px; height: 10px;
        background: rgba(255,255,255,.75); box-shadow: 0 0 10px rgba(255,255,255,.5);
      }

      /* ── OLD logo-wrap style — uncomment block below & remove .halo class to revert ──
      .lp-mark .logo-wrap {
        width: 260px; height: 260px; background: rgba(255,255,255,.06);
        border-radius: 50%; display: grid; place-items: center;
        backdrop-filter: blur(6px); border: 1.5px solid rgba(255,255,255,.18);
        position: relative; z-index: 2;
      }
      .lp-mark .logo-wrap::before {
        content: ''; position: absolute; inset: -8px; border-radius: 50%;
        background: radial-gradient(circle at 50% 40%, rgba(212,217,128,.30), transparent 70%);
        z-index: -1;
      }
      ── */

      /* ── NEW logo-wrap style ── */
      .lp-mark .logo-wrap {
        width: 260px; height: 260px; background: rgba(255,255,255,.07);
        border-radius: 50%; display: grid; place-items: center;
        backdrop-filter: blur(8px); border: 1.5px solid rgba(255,255,255,.22);
        position: relative; z-index: 2;
        animation: logo-halo-pulse 4s ease-in-out infinite;
      }
      .lp-mark .logo-wrap::before {
        content: ''; position: absolute; inset: -10px; border-radius: 50%;
        background: radial-gradient(circle at 50% 38%, rgba(212,217,128,.38), transparent 68%);
        z-index: -1; animation: logo-halo-glow 4s ease-in-out infinite;
      }

      /* Spinning arc rings — remove .spin-arc elements in JSX to revert */
      .lp-mark .spin-arc {
        position: absolute; border-radius: 50%; border: 2px solid transparent;
        z-index: 1; pointer-events: none;
      }
      .lp-mark .spin-arc.a1 {
        width: 288px; height: 288px;
        border-top-color: rgba(212,217,128,.80);
        border-right-color: rgba(212,217,128,.18);
        animation: lp-spin 3.8s linear infinite;
      }
      .lp-mark .spin-arc.a2 {
        width: 278px; height: 278px;
        border-bottom-color: rgba(155,192,113,.60);
        border-left-color: rgba(155,192,113,.12);
        animation: lp-spin 5.5s linear infinite reverse;
      }

      /* ── OLD logo image style — remove className & uncomment inline style to revert ──
         style={{ width: "88%", maxWidth: 220, height: "auto", objectFit: "contain", opacity: 0.95 }}
      ── */

      /* ── NEW logo image animation ── */
      @keyframes logo-breathe {
        0%, 100% { transform: translateY(0)    scale(1);     filter: drop-shadow(0  6px 18px rgba(212,217,128,.28)); }
        50%       { transform: translateY(-11px) scale(1.03); filter: drop-shadow(0 20px 38px rgba(212,217,128,.58)); }
      }
      @keyframes logo-halo-pulse {
        0%, 100% { box-shadow: 0 0 0 1.5px rgba(212,217,128,.25), 0 0 28px rgba(93,143,34,.14); }
        50%       { box-shadow: 0 0 0 3.5px rgba(212,217,128,.55), 0 0 65px rgba(93,143,34,.30); }
      }
      @keyframes logo-halo-glow {
        0%, 100% { opacity: .7; transform: scale(1);    }
        50%       { opacity: 1;  transform: scale(1.06); }
      }
      .lp-mark .logo-breathe {
        animation: logo-breathe 5s ease-in-out infinite;
        width: 88%; max-width: 220px; height: auto; object-fit: contain;
      }

      .lp-mark .bexyl-tag {
        position: absolute; bottom: -14px; background: var(--brown-900);
        color: var(--lime-400); font-size: 10px; font-weight: 800;
        letter-spacing: .45em; padding: 6px 14px; border-radius: 99px;
        border: 1px solid rgba(212,217,128,.35);
      }
      .wave-divider {
        display: block; width: 100%; height: 64px; margin-bottom: -1px;
        position: relative; z-index: 2;
      }

      /* Eyebrow + section heading */
      .eyebrow {
        font-size: 11px; font-weight: 800; text-transform: uppercase;
        letter-spacing: .14em; color: var(--green-400);
        display: inline-flex; align-items: center; gap: 10px;
      }
      .eyebrow::before {
        content: ''; width: 24px; height: 2px; background: var(--green-400); border-radius: 99px;
      }
      .section-title {
        font-size: clamp(26px, 3.2vw, 38px); font-weight: 900;
        color: var(--green-600); margin: 10px 0 14px;
        line-height: 1.15; letter-spacing: -.01em;
      }
      .section-lead {
        font-size: 15.5px; color: var(--brown-500); line-height: 1.75; max-width: 680px;
      }

      /* Decision Path */
      .path-section { max-width: 1200px; margin: 0 auto; padding: 64px 28px 24px; }
      .path-header {
        display: grid; grid-template-columns: 1fr auto; gap: 28px;
        align-items: end; margin-bottom: 44px;
      }
      .meta-card {
        background: #fff; border: 1.5px solid var(--lime-300); border-radius: 16px;
        padding: 14px 18px; display: flex; align-items: center; gap: 12px;
        box-shadow: 0 4px 18px rgba(75,117,24,.06);
      }
      .meta-card .dot {
        width: 8px; height: 8px; border-radius: 50%; background: var(--green-500);
        animation: lp-pulse 1.6s ease-out infinite;
      }
      .meta-card span { font-size: 12.5px; font-weight: 700; color: var(--green-600); }

      .path-flow {
        position: relative; display: grid; grid-template-columns: repeat(4, 1fr);
        gap: 0; align-items: start; padding: 12px 0 8px;
      }
      .path-flow .vine {
        position: absolute; top: 48px; left: 7%; right: 7%; height: 3px;
        background-image: radial-gradient(circle, var(--green-400) 1.5px, transparent 2px);
        background-size: 12px 3px; background-repeat: repeat-x;
        background-position: 0 50%; opacity: .55;
      }
      .path-node {
        display: flex; flex-direction: column; align-items: center;
        text-align: center; position: relative; z-index: 2; padding: 0 6px;
      }
      .path-blob {
        width: 100px; height: 100px; display: grid; place-items: center;
        position: relative; margin-bottom: 14px;
        transition: transform .35s cubic-bezier(.2,.7,.2,1);
      }
      .path-blob::before {
        content: ''; position: absolute; inset: 0;
        background: linear-gradient(135deg, var(--green-500), var(--green-700));
        box-shadow:
          0 12px 30px rgba(75,117,24,.32),
          inset 4px 4px 12px rgba(255,255,255,.18),
          inset -4px -6px 14px rgba(0,0,0,.18);
        transition: border-radius .8s cubic-bezier(.4,0,.2,1);
      }
      .path-blob.b1::before { border-radius: 62% 38% 55% 45% / 55% 50% 50% 45%; }
      .path-blob.b2::before { border-radius: 45% 55% 40% 60% / 50% 60% 40% 50%; }
      .path-blob.b3::before { border-radius: 58% 42% 60% 40% / 45% 55% 45% 55%; }
      .path-blob.b4::before { border-radius: 50% 50% 35% 65% / 60% 40% 60% 40%; }
      .path-blob:hover { transform: translateY(-6px) rotate(-3deg); }
      .path-blob:hover.b1::before { border-radius: 50% 50% 45% 55% / 45% 60% 40% 55%; }
      .path-blob:hover.b2::before { border-radius: 60% 40% 55% 45% / 60% 50% 50% 40%; }
      .path-blob:hover.b3::before { border-radius: 40% 60% 45% 55% / 55% 45% 55% 45%; }
      .path-blob:hover.b4::before { border-radius: 55% 45% 60% 40% / 50% 55% 45% 50%; }
      .path-blob .icon-pop { position: relative; z-index: 2; color: #fff; display: grid; place-items: center; }
      .path-blob .step {
        position: absolute; top: -8px; right: -8px; width: 28px; height: 28px;
        background: var(--lime-400); color: #4a5500; font-size: 12px; font-weight: 900;
        border-radius: 50%; display: grid; place-items: center;
        border: 2px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,.18); z-index: 3;
      }
      .path-node h3 {
        font-size: 15px; font-weight: 800; color: var(--brown-700);
        margin: 0 0 6px; line-height: 1.25; min-height: 38px;
        display: flex; align-items: center;
      }
      .path-node p {
        font-size: 12.5px; color: var(--brown-500); line-height: 1.55;
        margin: 0; max-width: 200px;
      }
      .path-caption {
        margin-top: 36px; display: grid;
        grid-template-columns: auto 1fr auto; gap: 22px; align-items: center;
        background: #fff; border: 1px solid var(--lime-300); border-radius: 14px;
        padding: 18px 22px; box-shadow: 0 4px 18px rgba(75,117,24,.05);
      }
      .path-caption .tree-icon {
        width: 38px; height: 38px; border-radius: 11px;
        background: var(--lime-300); color: var(--green-600);
        display: grid; place-items: center; border: 1.5px solid var(--lime-400);
      }
      .path-caption .ct-title { font-size: 14px; font-weight: 800; color: var(--brown-700); margin-bottom: 2px; }
      .path-caption .ct-body { font-size: 13px; color: var(--brown-500); line-height: 1.65; }
      .path-caption .ct-body strong { color: var(--green-600); }
      .path-caption .ct-cta {
        background: var(--green-600); color: #fff; padding: 10px 18px;
        border-radius: 10px; font-size: 12.5px; font-weight: 700;
        display: inline-flex; align-items: center; gap: 8px;
        white-space: nowrap; cursor: pointer; border: none; font-family: inherit;
        transition: background .2s, transform .2s, box-shadow .2s;
      }
      .path-caption .ct-cta:hover {
        background: var(--green-700); transform: translateX(3px);
        box-shadow: 0 6px 18px rgba(75,117,24,.32);
      }

      /* Features (organic blobs) */
      .features { max-width: 1200px; margin: 0 auto; padding: 48px 28px 24px; position: relative; }
      .features-head { margin-bottom: 42px; }
      .feature-row {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px;
        align-items: start; position: relative;
      }
      .feature {
        position: relative; padding: 32px 26px 28px; background: #fff;
        transition: transform .4s cubic-bezier(.2,.7,.2,1), box-shadow .4s;
        isolation: isolate;
      }
      .feature.f1 { border-radius: 38% 62% 50% 50% / 35% 45% 55% 65%; }
      .feature.f2 { border-radius: 60% 40% 35% 65% / 55% 60% 40% 45%; margin-top: 36px; }
      .feature.f3 { border-radius: 52% 48% 60% 40% / 60% 35% 65% 40%; }
      .feature::before {
        content: ''; position: absolute; inset: 0; border-radius: inherit;
        border: 2px solid var(--lime-400); pointer-events: none; z-index: 1;
        transition: border-color .35s;
      }
      .feature::after {
        content: ''; position: absolute; inset: 14px; border-radius: inherit;
        background: radial-gradient(circle at 30% 20%, rgba(155,192,113,.15), transparent 60%);
        pointer-events: none; z-index: -1;
      }
      .feature:hover { transform: translateY(-12px) rotate(-1.5deg); box-shadow: 0 24px 50px rgba(75,117,24,.18); }
      .feature.f2:hover { transform: translateY(-12px) rotate(1.5deg); }
      .feature:hover::before { border-color: var(--green-500); }
      .feature .leaf-icon {
        width: 52px; height: 52px; border-radius: 50% 50% 50% 0;
        background: linear-gradient(135deg, var(--green-500), var(--green-700));
        display: grid; place-items: center; color: #fff;
        margin-bottom: 16px; transform: rotate(-45deg);
        box-shadow: 0 8px 22px rgba(75,117,24,.32);
        position: relative; z-index: 2;
      }
      .feature .leaf-icon svg { transform: rotate(45deg); }
      .feature h3 {
        font-size: 19px; font-weight: 800; color: var(--green-700);
        margin: 0 0 10px; position: relative; z-index: 2;
      }
      .feature p {
        font-size: 14px; color: var(--brown-500); line-height: 1.7;
        margin: 0 0 22px; position: relative; z-index: 2;
      }
      .feature p strong { color: var(--brown-700); }
      .feature-cta {
        display: inline-flex; align-items: center; gap: 8px;
        background: linear-gradient(110deg, var(--lime-400) 0%, #edf07e 40%, var(--lime-400) 60%, var(--lime-500) 100%);
        background-size: 250% auto;
        color: #4a5500; padding: 11px 20px; border-radius: 99px;
        font-size: 13px; font-weight: 700;
        transition: gap .25s, background-position .55s ease, box-shadow .25s, transform .25s;
        box-shadow: 0 4px 14px rgba(75, 117, 24, 1);
        position: relative; z-index: 2;
      }
      .feature-cta:hover {
        gap: 14px; transform: scale(1.04);
        background-position: right center;
        box-shadow: 0 8px 24px rgba(212,217,128,.72);
      }
      .features .float-leaf {
        position: absolute; pointer-events: none;
        color: var(--green-300); opacity: .35;
        animation: lp-floatY 8s ease-in-out infinite;
      }
      .features .float-leaf.l1 { top: 10px; left: -8px; }
      .features .float-leaf.l2 { bottom: -20px; right: 4%; animation-delay: 1.5s; opacity: .25; }

      /* Disclaimer (caution-tape) */
      .disclaimer-section { max-width: 1200px; margin: 0 auto; padding: 28px 28px 56px; }
      .disclaimer {
        position: relative;
        background: linear-gradient(110deg, #fff8d8 0%, #fff8d8 60%, #fff 100%);
        border-radius: 14px; padding: 20px 28px 20px 90px;
        border: 1.5px solid #e9d97a;
        box-shadow: 0 4px 18px rgba(212,178,17,.10); overflow: hidden;
      }
      .disclaimer .stripes {
        position: absolute; left: 0; top: 0; bottom: 0; width: 64px;
        background-image: repeating-linear-gradient(
          -45deg, #f0c93f 0px, #f0c93f 16px, #1a1a1a 16px, #1a1a1a 32px);
      }
      .disclaimer .stripes::after {
        content: '!'; position: absolute; inset: 0;
        display: grid; place-items: center;
        font-size: 28px; font-weight: 900; color: #fff;
        text-shadow: 0 2px 4px rgba(0,0,0,.4); background: rgba(0,0,0,.18);
      }
      .disclaimer h4 {
        font-size: 14px; font-weight: 900; color: var(--brown-700);
        margin: 0 0 6px; letter-spacing: .04em; text-transform: uppercase;
      }
      .disclaimer p { font-size: 13.5px; color: var(--brown-500); line-height: 1.7; margin: 0; }
      .disclaimer p strong { color: var(--brown-700); }

      /* ── Hamburger / mobile nav ── */
      .bx-hamburger {
        display: none; background: none; border: none; cursor: pointer;
        padding: 8px; border-radius: 8px; color: var(--brown-500);
        transition: background .2s;
        align-items: center; justify-content: center; flex-shrink: 0;
      }
      .bx-hamburger:hover { background: rgba(75,117,24,.08); }
      .bx-mobile-menu {
        display: none; flex-direction: column; gap: 2px;
        background: #fff; border-top: 1px solid var(--lime-300);
        padding: 10px 16px 16px;
        box-shadow: 0 8px 24px rgba(0,0,0,.08);
      }
      .bx-mobile-menu.open { display: flex; }
      .bx-mobile-menu .nav-pill {
        width: 100%; justify-content: flex-start !important;
        padding: 12px 16px; font-size: 15px; border-radius: 10px;
      }

      /* ── Responsive ── */
      @media (max-width: 768px) {
        .bx-hamburger { display: flex !important; }
        .bx-desktop-nav { display: none !important; }
        .bx-nav-inner { height: 64px !important; padding: 0 16px !important; }
        .bx-nav-inner .bx-brand img { height: 50px !important; width: 140px !important; }

        .lp-hero-grid {
          grid-template-columns: 1fr !important;
          padding: 36px 20px 0 !important;
          gap: 0 !important;
        }
        .lp-mark { display: none !important; }
        .lp-hero-ctas { flex-direction: column !important; gap: 10px !important; }
        .lp-hero-ctas button { width: 100% !important; justify-content: center !important; }

        .path-section { padding: 36px 20px 16px !important; }
        .path-header { grid-template-columns: 1fr !important; }
        .meta-card { display: none !important; }
        .path-flow { grid-template-columns: 1fr 1fr !important; gap: 20px 12px !important; }
        .path-flow .vine { display: none !important; }
        .path-caption { grid-template-columns: 1fr !important; gap: 12px !important; }
        .path-caption .ct-cta { width: 100% !important; justify-content: center !important; }

        .features { padding: 32px 20px 16px !important; }
        .feature-row { grid-template-columns: 1fr !important; gap: 20px !important; }
        .feature { border-radius: 20px !important; margin-top: 0 !important; }

        .disclaimer-section { padding: 16px 20px 40px !important; }

        .footer-inner { padding: 24px 20px 20px !important; }
        .footer-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
      }
      @media (max-width: 480px) {
        .path-flow { grid-template-columns: 1fr !important; }
        .lp-hero-grid { padding: 24px 16px 0 !important; }
      }

      /* Footer */
      .footer { background: var(--brown-700); color: rgba(255,255,255,.75); margin-top: 40px; }
      .footer-stripe { height: 4px; background: linear-gradient(90deg, var(--lime-400), var(--green-300), var(--lime-400)); }
      .footer-inner { max-width: 1200px; margin: 0 auto; padding: 36px 28px 24px; }
      .footer-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 40px; align-items: start; }
      .footer-title { font-size: 15px; font-weight: 700; color: var(--lime-400); margin-bottom: 8px; }
      .footer-sub { font-size: 13px; line-height: 1.6; }
      .footer-meta { font-size: 12px; margin-top: 10px; color: rgba(255,255,255,.5); }
      .footer-label {
        font-size: 11px; font-weight: 700; text-transform: uppercase;
        letter-spacing: .08em; color: var(--green-300); margin-bottom: 12px;
      }
      .footer-link {
        display: block; font-size: 13px; color: rgba(255,255,255,.7);
        margin-bottom: 7px; transition: color .2s, transform .2s;
      }
      .footer-link:hover { color: var(--lime-400); transform: translateX(3px); }
      /* OLD footer-eu badge style — uncomment block to revert:
      .footer-eu {
        background: #fff; border-radius: 6px; padding: 6px 10px; margin-top: 14px;
        display: inline-flex; align-items: center; gap: 10px; width: max-content;
      }
      .footer-eu .eu-flag {
        width: 36px; height: 24px; background: #003399; border-radius: 3px;
        display: grid; place-items: center; color: #FFCC00; font-size: 16px;
      }
      .footer-eu small {
        font-size: 9.5px; line-height: 1.25; color: #1a1a1a;
        font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
      }
      */
      .eu-funded-img {
        display: block; margin-top: 14px; width: 160px; height: auto;
      }
      .footer-admin {
        font-size: 12px; color: rgba(255,255,255,.35);
        display: block; margin-top: 14px; transition: color .2s;
      }
      .footer-admin:hover { color: rgba(255,255,255,.6); }
      .footer-bottom {
        border-top: 1px solid rgba(212,217,128,.2);
        margin-top: 24px; padding-top: 16px;
        font-size: 11px; color: rgba(255,255,255,.4); line-height: 1.6;
      }
    `}</style>
  );
}

// ── Bexyl leaf mark (small, used in nav) ──────────────────────
function BexylLeafmark({ size = 46 }) {
  return (
    <svg viewBox="0 0 46 46" width={size} height={size} aria-hidden="true">
      <path d="M10 40 Q22 22 30 8" stroke="#5C4B00" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <path d="M30 40 Q24 26 16 8" stroke="#5C4B00" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <ellipse cx="30" cy="10" rx="6" ry="3.5" fill="#7DA72F" transform="rotate(-30 30 10)" />
      <ellipse cx="22" cy="6"  rx="5" ry="3"   fill="#9BC071" transform="rotate(20 22 6)" />
      <ellipse cx="14" cy="9"  rx="4" ry="2.6" fill="#7DA72F" transform="rotate(-25 14 9)" />
      <ellipse cx="26" cy="14" rx="4" ry="2.6" fill="#4B7518" transform="rotate(15 26 14)" />
    </svg>
  );
}

// ── Navbar ────────────────────────────────────────────────────
function Navbar({ active = "home" }) {
  const { user } = useAuth();
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 14);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const canViewCatalog = user?.role === "risk_manager" || user?.role === "admin";

  const NavLinks = () => (
    <>
      <a href="/"        className={`nav-pill${active === "home"    ? " active" : ""}`}>Home</a>
      <a href="/search"  className={`nav-pill${active === "search"  ? " active" : ""}`}>Search</a>
      <a href="/info"    className={`nav-pill${active === "about"   ? " active" : ""}`}>About</a>
      {canViewCatalog && (
        <a href="/catalog" className={`nav-pill${active === "catalog" ? " active" : ""}`}>Catalog</a>
      )}
    </>
  );

  return (
    <nav className={`bx-nav${scrolled ? " scrolled" : ""}`} data-screen-label="nav" style={{ position: "relative" }}>
      <div className="bx-nav-inner">
        <a href="/" className="bx-brand">
          <img
            src="/logo.png"
            alt="BeXyl logo"
            style={{ height: 70, width: 200, objectFit: "contain" }}
            onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div style={{ display: "none", alignItems: "center", gap: 8 }}>
            <div className="bx-brand-mark"><BexylLeafmark /></div>
            <div className="bx-brand-text">BEXYL<small>BEYOND XYLELLA</small></div>
          </div>
        </a>

        {/* Desktop nav links */}
        <div className="bx-desktop-nav" style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <NavLinks />
        </div>

        {/* Hamburger button — only on mobile */}
        <button
          className="bx-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`bx-mobile-menu${menuOpen ? " open" : ""}`} role="navigation">
        <NavLinks />
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────
function Hero({ onSearch, onLearnMore }) {
  return (
    <section className="lp-hero" data-screen-label="hero">
      <div className="lp-hero-deco" aria-hidden="true">
        <svg className="rings-tr" width="540" height="440" viewBox="0 0 540 440">
          <circle cx="430" cy="100" r="180" stroke="#D4D980" strokeWidth="1.5" fill="none" />
          <circle cx="430" cy="100" r="120" stroke="#D4D980" strokeWidth="1"   fill="none" />
          <circle cx="430" cy="100" r="70"  stroke="#D4D980" strokeWidth="1"   fill="none" />
          <circle cx="240" cy="320" r="80"  stroke="#9BC071" strokeWidth="1"   fill="none" />
        </svg>
        <svg className="rings-bl" width="320" height="320" viewBox="0 0 320 320">
          <circle cx="80" cy="240" r="120" stroke="#D4D980" strokeWidth="1.5" fill="none" />
          <circle cx="80" cy="240" r="64"  stroke="#9BC071" strokeWidth="1"   fill="none" />
        </svg>
        <div className="orb" style={{ width: 280, height: 280, background: "rgba(212,217,128,.18)", top: 60, left: -80, animation: "lp-drift 14s ease-in-out infinite" }} />
        <div className="orb" style={{ width: 200, height: 200, background: "rgba(155,192,113,.20)", bottom: 120, right: "30%", animation: "lp-drift 18s 2s ease-in-out infinite" }} />
      </div>

      <div className="lp-hero-grid">
        <div>
          <h1>
            Decision Support System<br />
             for{" "}
            <span className="accent">Xylella&nbsp;fastidiosa</span>{" "}
            management
          </h1>
          <p className="lead">
            A decision-support system helping farmers, nurseries, and risk managers identify
            practical Integrated Pest Management (IPM) schemes for managing{" "}
            <span className="species">Xylella fastidiosa</span>{" "}
            in almond, grapevine, and olive systems across Europe.
          </p>
          <div className="lp-hero-ctas">
            <button onClick={onSearch} className="btn-primary">
              <svg className="search" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
              </svg>
              Start guided search
            </button>
            <button onClick={onLearnMore} className="btn-ghost">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
              </svg>
              Learn more
            </button>
          </div>
        </div>

        <div className="lp-mark">
          {/* Orbit rings — remove r0 to revert to 2-ring look */}
          <div className="orbit r0"><span className="seed" /></div>
          <div className="orbit r1"><span className="seed" /></div>
          <div className="orbit r2"><span className="seed" /></div>

          {/* Spinning arc halos — remove both to revert */}
          <div className="spin-arc a1" aria-hidden="true" />
          <div className="spin-arc a2" aria-hidden="true" />

          <div className="logo-wrap">
            {/* ── NEW animated logo — swap className↔style comment block below to revert ── */}
            <img
              src="/logo1.png"
              alt="BeXyl project logo"
              className="logo-breathe"
              onError={e => e.target.style.display = "none"}
            />
            {/* ── OLD static logo style (restore by replacing className with this style prop):
            style={{ width: "88%", maxWidth: 220, height: "auto", objectFit: "contain", opacity: 0.95 }}
            ── */}

            {/* Original SVG fallback (kept for reference)
            <svg width="180" height="180" viewBox="0 0 200 200" aria-hidden="true">
              <path d="M40 180 Q90 110 130 30"  stroke="#fff" strokeWidth="11" fill="none" strokeLinecap="round" />
              <path d="M130 180 Q105 120 70 30" stroke="#fff" strokeWidth="11" fill="none" strokeLinecap="round" />
              <ellipse cx="130" cy="32" rx="20" ry="11" fill="#fff" transform="rotate(-30 130 32)" />
              <ellipse cx="98"  cy="20" rx="17" ry="9"  fill="#fff" transform="rotate(25 98 20)" />
              <ellipse cx="68"  cy="28" rx="14" ry="8"  fill="#fff" transform="rotate(-20 68 28)" />
              <ellipse cx="116" cy="52" rx="13" ry="7"  fill="#fff" transform="rotate(15 116 52)" />
              <ellipse cx="86"  cy="50" rx="12" ry="7"  fill="#fff" transform="rotate(-15 86 50)" />
            </svg>
            */}
          </div>
        </div>
      </div>

      <svg className="wave-divider" viewBox="0 0 1200 64" preserveAspectRatio="none">
        <path d="M0,24 C200,64 360,4 560,30 C740,56 900,8 1100,28 L1200,32 L1200,64 L0,64 Z" fill="#F6F9F2" />
        <path d="M0,28 C200,66 360,8 560,34 C740,58 900,12 1100,30 L1200,34"
              fill="none" stroke="#D4D980" strokeWidth="2.5" opacity=".8" />
      </svg>
    </section>
  );
}

// ── Decision Path ─────────────────────────────────────────────
const PATH_NODES = [
  {
    n: 1, cls: "b2", title: "User profile",
    // desc: "Farmer, nursery worker, or risk manager — context shapes the answer.",
    desc: "Farmer, nursery worker, or risk manager.",
    icon: (
      <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    n: 2, cls: "b1", title: "Crop",
    // desc: "Almond, olive, or grapevine — the system narrows to validated schemes.",
    desc: "Almond, olive, or grapevine.",
    icon: (
      <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z" />
      </svg>
    ),
  },
  {
    n: 3, cls: "b3", title: "Management objective",
    // desc: "Prevention, containment, or active management of the outbreak.",
    desc: "Prevention, monitoring, or active intervention of the outbreak.",
    icon: (
      <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    n: 4, cls: "b4", title: "Intensity level",
    desc: "Low, medium, or high matching what your operation can run.",
    icon: (
      <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="9"  x2="21" y2="9" />
        <line x1="3" y1="15" x2="21" y2="15" />
        <circle cx="7"  cy="9"  r="2.5" fill="currentColor" />
        <circle cx="17" cy="15" r="2.5" fill="currentColor" />
      </svg>
    ),
  },
];

function DecisionPath({ onSearch }) {
  return (
    <section className="path-section reveal" id="decision-path">
      <div className="path-header">
        <div>
          <div className="eyebrow">The DSS decision tree</div>
          <h2 className="section-title">Four questions. One personalised plan.</h2>
          <p className="section-lead">
            The DSS guides users through a simple decision tree. By selecting the{" "}
            <strong style={{ color: "var(--green-600)" }}>user profile, crop type, management objective,</strong>{" "}
            and{" "}
            <strong style={{ color: "var(--green-600)" }}>level of implementation effort</strong>,
            users receive a set of IPM schemes adapted to their situation.
          </p>
        </div>
        <div className="meta-card">
          <span className="dot" />
          <span>4 STEPS . 1 MIN</span>
        </div>
      </div>

      <div className="path-flow">
        <div className="vine" />
        {PATH_NODES.map(node => (
          <div className="path-node" key={node.n}>
            <div className={`path-blob ${node.cls}`}>
              <span className="step">{node.n}</span>
              <div className="icon-pop">{node.icon}</div>
            </div>
            <h3>{node.title}</h3>
            <p>{node.desc}</p>
          </div>
        ))}
      </div>

      <div className="path-caption">
        <div className="tree-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="8" y="1" width="8" height="5" rx="1" />
            <rect x="1" y="17" width="7" height="5" rx="1" />
            <rect x="16" y="17" width="7" height="5" rx="1" />
            <path d="M12 6v4M12 10H4v7M12 10h8v7" />
          </svg>
        </div>
        <div>
          <div className="ct-title">Expert-ranked set of IPM schemes</div>
          <div className="ct-body">
            At the end of the tree, the DSS returns a tailored list of schemes{" "}
            <strong>ordered by effectiveness and feasibility</strong> for your exact situation.
          </div>
        </div>
        <button className="ct-cta" onClick={onSearch}>
          Start now
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  );
}

// ── Feature Blobs ─────────────────────────────────────────────
const FEATURES = [
  {
    cls: "f1",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    title: "The BeXyl Project",
    desc: (
      <>
        The BeXyl Project gathers the top worldwide scientists and stakeholders to find new
        strategies to mitigate the impact of the <strong><em>Xylella fastidiosa</em></strong>{" "}
        outbreaks in the EU.
      </>
    ),
    cta: "Learn more",
    href: "https://bexylproject.org",
    external: true,
    ctaIcon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    ),
  },
  {
    cls: "f2",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
    ),
    title: "What is the DSS?",
    desc: (
      <>
        The DSS helps users identify suitable IPM schemes for the management of{" "}
        <strong><em>Xylella fastidiosa</em></strong> across Mediterranean tree crops:
        almond, olive and grapevine.
      </>
    ),
    cta: "Explore DSS",
    href: "/info#dss",
    ctaIcon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    ),
  },
  {
    cls: "f3",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <polyline points="9 12 10.5 13.5 13 11" />
        <polyline points="9 16 10.5 17.5 13 15" />
      </svg>
    ),
    title: "IPM Schemes",
    desc: (
      <>
        IPM schemes include combined management options for{" "}
        <strong>prevention, monitoring, and intervention</strong>, that work together to support{" "}
        <strong><em>Xylella fastidiosa</em></strong> management.
      </>
    ),
    cta: "View schemes",
    href: "/info#ipm",
    ctaIcon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
];

function FeatureBlobs() {
  return (
    <section className="features reveal">
      <svg className="float-leaf l1" width="58" height="58" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z" />
      </svg>
      <svg className="float-leaf l2" width="46" height="46" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z" />
      </svg>

      <div className="features-head">
        <div className="eyebrow">Explore the platform</div>
        <h2 className="section-title">Three doors into the work.</h2>
      </div>

      <div className="feature-row">
        {FEATURES.map(f => (
          <article key={f.title} className={`feature ${f.cls} reveal`}>
            <div className="leaf-icon">{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
            <a
              className="feature-cta"
              href={f.href}
              {...(f.external ? { target: "_blank", rel: "noreferrer" } : {})}
            >
              {f.cta}
              {f.ctaIcon}
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

// ── Disclaimer ────────────────────────────────────────────────
function Disclaimer() {
  return (
    <section className="disclaimer-section reveal">
      <div className="disclaimer">
        <div className="stripes" aria-hidden="true" />
        <h4>Disclaimer</h4>
        <p>
          This DSS is intended for use in areas where <strong><em>Xylella fastidiosa</em></strong>
          {" "}is already established and where IPM-based management strategies may be allowed.
          It is <strong>not designed</strong> for pest-free areas, eradication areas, or buffer
          zones where mandatory phytosanitary measures apply. Always verify compliance with
          local regulations before applying any scheme.
        </p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="footer">
      <div className="footer-stripe" />
      <div className="footer-inner">
        <div className="footer-grid">
          <div>
            <div className="footer-title">BeXyl WP7 · IPM Decision Support System</div>
            <div className="footer-sub">EU Horizon Europe · Xylella fastidiosa management</div>
            <div className="footer-meta">© {year} BeXyl Consortium. All rights reserved.</div>
          </div>
          <div>
            <div className="footer-label">Project links</div>
            {[
              ["BeXyl project website", "https://bexylproject.org"],
              ["About BeXyl", "https://bexylproject.org/about-bexyl-project/"],
              ["EU Horizon Europe", "https://research-and-innovation.ec.europa.eu/funding/funding-opportunities/funding-programmes-and-open-calls/horizon-europe_en"],
              ["Grant Agreement No. 101060593."],
            ].map(([label, url]) => (
              <a key={label} href={url} target="_blank" rel="noreferrer" className="footer-link">
                {label}
              </a>
            ))}
            {/* OLD footer-eu badge — uncomment to revert:
            <div className="footer-eu">
              <div className="eu-flag">★</div>
              <small>Funded by<br />the European Union</small>
            </div>
            */}
            <img src="/eu1.jpg" alt="Funded by the European Union" className="eu-funded-img" />
          </div>
          <div>
            <div className="footer-label">Platform</div>
            <div className="footer-sub" style={{ color: "rgba(255,255,255,.7)", marginBottom: 6 }}>IPM DSS v1.0</div>
            <div className="footer-sub" style={{ color: "rgba(255,255,255,.7)" }}>Crops: Almond · Olive · Grapevine</div>
            <a href="/admin" className="footer-admin">Admin access</a>
            <a href="/catalog" className="footer-admin">Risk Manager</a>
          </div>
        </div>
        <div className="footer-bottom">
          This platform is part of the BeXyl project, funded by the European Union under{" "}
          <strong>Horizon Europe Programme.</strong> Grant agreement No. 101060593.
        </div>
      </div>
    </footer>
  );
}

// ── Scroll-reveal hook ────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const reduced = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reveals = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || reduced) {
      reveals.forEach(r => r.classList.add("in"));
      return;
    }
    document.documentElement.classList.add("js-anim");
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });
    reveals.forEach(r => io.observe(r));
    // Safety fallback if IO never ticks
    const t = setTimeout(() => reveals.forEach(r => r.classList.add("in")), 2500);
    return () => {
      io.disconnect();
      clearTimeout(t);
      document.documentElement.classList.remove("js-anim");
    };
  }, []);
}

// ── Landing page ──────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  useScrollReveal();

  return (
    <div className="bx-root" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <GlobalStyles />
      <Navbar active="home" />
      <Hero onSearch={() => navigate("/search")} onLearnMore={() => navigate("/info")} />
      <DecisionPath onSearch={() => navigate("/search")} />
      <FeatureBlobs />
      <Disclaimer />
      <div style={{ flex: 1 }} />
      <Footer />
      <QuestionnaireBanner />
    </div>
  );
}
